import { Component, OnInit, ViewChild, TemplateRef, Input, ViewEncapsulation, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, AfterViewChecked } from '@angular/core';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import { IDataTableService } from '../common/interfaces/IDataTableService.interface';
import { debounceTime, distinctUntilChanged, filter, finalize } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import moment from 'moment';

import { IColumn, CellType, ColumnType, ColumnSearchFieldType, ColumnSearchEventData } from '../common/interfaces/IColumn.interface';
import { DataTableFilter, OrderDirection, FilterType, Filter, RowChanges, FilterOperator } from '../common/models/DataTableFilter.model';
import { removeIfExists, max, hasValue } from '../common/functions/js-linq';
import { IPagedDataTable } from '../common/interfaces/IPagedDataTable.interface';
import { KeyValuePair } from "../common/models/KeyValuePair.model";
import { ActionButton } from './action-buttons/action-button.model';
import { KeyboardCode } from '../common/enums/keyboard-code';
import { clone } from '../common/functions/common-helpers';
import { CurrentUser } from '../common/services/current-user.service';
import { ActionButtonsComponent } from './action-buttons/action-buttons.component';
import { DatatableColumnsConstants } from '../common/constants/datatable-columns.constant';
import { AdvanceFilterConstants } from '../common/constants/advance-filter.constant';
import { FilterComparator, OrderComparator } from './comparator.model';
import { CpqNumberOptions } from '../directives/validator/cpq-number-options.model';
import { ILookup } from '../common/interfaces/ILookup.interface';
import { TranslationService } from '../common/services/translation.service';
import { SelectType } from '../cpq-select/cpq-select.component';
import {Subscription} from "rxjs";
import { ElementResizeObserverService } from '../common/services/element-resize-observer.service';
import { FormControl } from '@angular/forms';
const isoDateTimeFormat = 'YYYY-MM-DD[T]HH:mm:ss.SSS';

@Component({
    selector: 'cpq-datatable',
    templateUrl: './cpq-datatable.component.html',
    styleUrls: ['./cpq-datatable.component.scss', './cpq-datatable-header-and-footer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class CpqDatatableComponent implements OnInit, OnDestroy, OnChanges {
  public loadingInProgress: boolean;

  @ViewChild(DatatableComponent, { static: true }) table: DatatableComponent;
  @ViewChild('actionButtonsComponent') actionButtonsComponent: ActionButtonsComponent;

  @Input('columns') columns = new Array<IColumn>();
  @Input('rows') rows: Array<any> = new Array<any>();
  @Input('filter') filter: DataTableFilter;
  @Input('service') service: IDataTableService<any>;
  @Input('actionButtons') actionButtons: Array<ActionButton>;
  @Input('columnMode') columnMode: ColumnMode | "standard" | "flex" | "force" = 'force';
  @Input('scrollbarH') scrollbarH: boolean | string = 'true';
  @Input('getRowClass') getRowClass: (row: any) => string;
  @Input('showColumnSelection') showColumnSelection: boolean = true;
  @Input('showColumnSearchFields') showColumnSearchFields: boolean = false;
  @Input('showFilter') showFilter: boolean = true;
  @Input('showPager') showPager: boolean = true;
  @Input('hideHeader') hideHeader: boolean = false;
  @Input('showNumberOfItemsPerPage') showNumberOfItemsPerPage: boolean = true;
  /**
   * guid has to be provided for advanced filter
   * and saving column visibility state
   */
  @Input('guid') guid: string;
  /**
   * To turn Table cache on you should set 'Product Rules And Messages Client Caching - Products 2.0'
   * parameter to TRUE on Application Parameters page in setup
   */
  @Input('serverSideProcessing') serverSideProcessing = true; // if this input is false, filtering, pagination and sorting will be on client side
  /**
   * In filterTable() method if getRowsSubscription exists and this input is set to true
   * old request will be canceled before new is started
   */
  @Input('cancelOldGetRowsRequestOnNewRequest') cancelOldGetRowsRequestOnNewRequest = false;

  @Input('customParams') customParams: Array<KeyValuePair<string, any>>;

  /**
   * It is used only for client processing. So we can filter table on client with provided filterProperties.
   */
  @Input('filterProperties') filterProperties: Array<string> = [];
  @Input('tableId') tableId?= 'tableId';

  /**
   * If this input if true when you initialize cpq-datatable loading data will be skipped.
   * [Use case]: when we want to initialize datatable but filters are avaiable later and we want to call manualy filterTable()
   */
  @Input('skipFilteringOnInit') skipFilteringOnInit: boolean = false;
  /**
  * onRowsChanged: Every time when row in datatable has been added or deleted, this event will be triggered
  */
  @Output('onRowsChanged') onRowsChanged = new EventEmitter<IPagedDataTable<any>>();
  /**
  * onRowChanged: Every time when a property in datatable has been changed, this event will be triggered,
  */
  @Output('onRowChanged') onRowChanged = new EventEmitter<any>();
  @Output('onFilterRemove') onFilterRemove: EventEmitter<Filter> = new EventEmitter<Filter>();
  @Output('onFocusOut') onFocusOut = new EventEmitter<any>();
  @Output('onSorted') onSorted = new EventEmitter<any>();
  @Output('afterTableInit') afterTableInit = new EventEmitter<boolean>();
  @Input('showTotalNumbers') showTotalNumbers: boolean = true;
  @Input('rowIdentifier') rowIdentifier = 'id';
  @Output('columnSearchToggleChange')columnSearchToggleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Represent the current rows of the table. (it will not include rows that are not visible due to pagination)
   * Use this if you need to access the current, visible, rows of the table.
   */
  public tableRows = [];
  public tableColumns = new Array<IColumn>();
  public totalNumberOfRecords: number;
  public totalPages: number;
  public currentPage = 1;
  public modifiedRows = [];
  public tableWidth = 0;
  public columnType = ColumnType;
  public ColumnSearchFieldType = ColumnSearchFieldType;
  public yesNoSelectItems: Array<ILookup> = [
    { id: '1', text: this.translationService.translate('Msg.Yes') },
    { id: '0', text: this.translationService.translate('Msg.No') }
  ];
  public hideColumnSearchFunctionality: boolean = true;
  public SelectType = SelectType;

  private filterChangedSubscription: any;
  private columnSearchValueChangedSubscription = new BehaviorSubject<any>('');
  private userDateFormat = this.userSettings.dateFormats.display.dateInput;
  private previousScrollPosition = 0;
  private getRowsSubscription: Subscription;
  public searchControl = new FormControl<string>('');


  @ViewChild('defaultCellTmpl', { static: true }) defaultCellTmpl: TemplateRef<any>;
  @ViewChild('toggleCellTmpl', { static: true }) toggleCellTmpl: TemplateRef<any>;
  @ViewChild('editTmpl') editTmpl: TemplateRef<any>;
  @ViewChild('checkboxCellTmpl', { static: true }) checkboxCellTmpl: TemplateRef<any>;
  @ViewChild('twoStateCellTmpl', { static: true }) twoStateCellTmpl: TemplateRef<any>;
  @ViewChild('defaultHeaderTemplate', { static: true }) defaultHeaderTemplate: TemplateRef<any>;;

  constructor(private userSettings: CurrentUser, private translationService: TranslationService) { }

  public async ngOnInit() {
    this.initColumns();
    this.initColumnSearch();
    this.initQuickFilter();
    this.filter.initFilters(this.guid);
    this.table.sorts = [{ prop: this.filter.orderBy.name, dir: this.filter.orderBy.direction === OrderDirection.ASC ? 'asc' : 'desc' }];

    if (!this.skipFilteringOnInit) {
      if (this.serverSideProcessing) {
        this.filterTable();
      } else {
        this.initClientSideProcessing();
        this.updateScrollbars();
      }
    }

    ElementResizeObserverService.elementResize.subscribe(shouldResize => {
      if (shouldResize) {
        this.table.recalculate();
      }
    });

    this.initSubscriptions();
    this.afterTableInit.emit(true);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.initColumns();
    this.initFilters();
    this.validateDataProcessingParameters();
    if (!this.serverSideProcessing) {
      this.filterTable();
    }
  }

  public ngOnDestroy(): void {
    if (this.filterChangedSubscription) {
      this.filterChangedSubscription.unsubscribe();
    }
    if (this.columnSearchValueChangedSubscription) {
      this.columnSearchValueChangedSubscription.unsubscribe();
    }

    this.onRowChanged.unsubscribe();
    this.onRowsChanged.unsubscribe();
    
    if (this.getRowsSubscription) {
      this.getRowsSubscription.unsubscribe();
    }
  }

  public onSearchByDateChange(newValue, column) {
    if (!newValue) {
      column.searchValue = column.dateFromSearchValue;
      if (column.dateFromSearchValue) {
        column.dateToSearchValue = column.dateFromSearchValue;
        const dateToFilter = this.getDateFilter(column.prop, FilterOperator.SmallerOrEqual);
        dateToFilter.value = column.dateFromSearchValue
      }
    } else {
      column.dateToSearchValue = '';
    }
    this.filter.applyFilter();
  }

  private getDateFilter(columnName, filterOperator) {
    return this.filter.filters.find(filter => filter.type === FilterType.Date && filter.name === columnName && filter.operator === filterOperator);
  }

  private initSubscriptions() {
    this.filterChangedSubscription = this.filter.subscribeToChange(() => this.filterTable(true));

    this.columnSearchValueChangedSubscription.pipe(debounceTime(600)).subscribe((eventData: ColumnSearchEventData) => {
      const column = eventData.column;
      if (column && column.prop && column.searchFieldType !== ColumnSearchFieldType.date) {
        const filter = this.filter.filters.find(filter => column.filteringProp ? filter.name === column.filteringProp : filter.name === column.prop);
        filter.value = column.searchValue;
        if (column.searchFieldType === ColumnSearchFieldType.select || column.searchFieldType === ColumnSearchFieldType.twoState) {
          filter.valueText = eventData.searchText;
        }
        this.filter.applyFilter();
      }
    });
  }

  private initColumnSearch() {
    this.hideColumnSearchFunctionality = !this.showColumnSearchFields;
    const hideColumnSearchFunctionalityLocalSettings = JSON.parse(localStorage.getItem(this.guid + DatatableColumnsConstants.hideColumnSearchFunctionality));
    this.showColumnSearchFields = hideColumnSearchFunctionalityLocalSettings !== null ? !hideColumnSearchFunctionalityLocalSettings : false;

    this.setColumnSearchValuesIfExist();
  }

  private setColumnSearchValuesIfExist() {
    const localFilters = JSON.parse(localStorage.getItem(AdvanceFilterConstants.advanceFilterLocalStorage));

    if (localFilters && localFilters[this.guid]) {
      let dateFilters = []

      localFilters[this.guid].forEach(filter => {
        let column = this.columns.find(column => column.filteringProp ? column.filteringProp === filter.name : column.prop === filter.name);
        if (column && filter.type !== FilterType.Date) {
          column.searchValue = filter.value;
        } else if (column) {
          // since to and from filters have the same prop, we group them by it and distinguish them by the operator
          // ex. modifiedDate: [ { value: 2021/04/21, operator: FilterOperator.Bigger }, { value: 2021/05/21, operator: FilterOperator.Smaller }],
          //     createdOn: [ { value: 2021/03/19, operator: FilterOperator.Bigger }, { value: 2021/05/21, operator: FilterOperator.Smaller }]
          if (!dateFilters[filter.name]) {
            dateFilters[filter.name] = [];
          }

          dateFilters[filter.name].push({ value: filter.value, operator: filter.operator });
        }
      });

      this.applyExistingDateFilters(dateFilters);
    }
  }

  private applyExistingDateFilters(filters) {
    for (const filterName in filters) {
      let column = this.columns.find(column => filterName === column.prop);

      const localFilterValues = filters[column.prop];
      const dateFromFromLocalStorage = localFilterValues.find(filter => filter.operator === FilterOperator.BiggerOrEqual);
      const dateToFromLocalStorage = localFilterValues.find(filter => filter.operator === FilterOperator.SmallerOrEqual);
      const dateFromFromLocalStorageValue = dateFromFromLocalStorage?.value;
      const dateToFromLocalStorageValue = dateToFromLocalStorage?.value;
      let numberOfDaysDifference;
      if (dateFromFromLocalStorageValue && dateToFromLocalStorageValue) {
        numberOfDaysDifference = moment(dateToFromLocalStorageValue, this.userDateFormat, true).diff(moment(dateFromFromLocalStorageValue, this.userDateFormat), 'days');
      }
      column.isSearchByPeriod = numberOfDaysDifference !== 1;

      if (column.isSearchByPeriod) {
        if (dateFromFromLocalStorageValue && dateToFromLocalStorageValue) {
          column.searchValue = `${this.userSettings.dateToUserDateFormat(dateFromFromLocalStorageValue)} - ${this.userSettings.dateToUserDateFormat(dateToFromLocalStorageValue)}`;
        } else if (dateFromFromLocalStorageValue) {
          column.searchValue = `${this.translationService.translate('xsltAfter')} ${this.userSettings.dateToUserDateFormat(dateFromFromLocalStorageValue)}`
        } else {
          column.searchValue = `${this.translationService.translate('xsltBefore')} ${this.userSettings.dateToUserDateFormat(dateToFromLocalStorageValue)}`
        }
      } else {
        column.searchValue = this.userSettings.dateToUserDateFormat(dateFromFromLocalStorageValue);
      }

      column.dateFromSearchValue = this.userSettings.dateToUserDateFormat(dateFromFromLocalStorageValue);
      column.dateToSearchValue = !column.isSearchByPeriod ? column.dateFromSearchValue : this.userSettings.dateToUserDateFormat(dateToFromLocalStorageValue);

      const dateFromFilter = this.getDateFilter(column.prop, FilterOperator.BiggerOrEqual);
      const dateToFilter = this.getDateFilter(column.prop, FilterOperator.SmallerOrEqual);
      if (dateFromFilter) {
        dateFromFilter.value = column.dateFromSearchValue;
      }
      if (dateToFilter) {
        dateToFilter.value = column.dateToSearchValue;
      }
    }
  }

  public addNewRow(row: any, filterTable: boolean = true): void {
    if (!this.serverSideProcessing) {
      this.rows.push(row);
      if (filterTable) {
        this.filterTable();
      }
    }
  }

  public removeRow(predicate: (row: any) => boolean, filterTable: boolean = true) {
    if (!this.serverSideProcessing) {
      removeIfExists(this.rows, predicate);
      if (filterTable) {
        this.filterTable();
      }
    }
  }

  public updateRow(predicate: (row: any) => boolean, row: any, filterTable: boolean = true) {
    this.rows.splice(this.rows.findIndex(predicate), 1, row);
    if (filterTable) {
      this.filterTable();
    }
  }

  public onColumnsChanged(columns: Array<IColumn>) {
    this.columns = columns.slice();
    this.saveActiveColumns();
    this.initColumns();
  }

  public onHideColumnSearchFunctionalityChange() {
    this.showColumnSearchFields = !this.showColumnSearchFields;
    localStorage.setItem(this.guid + DatatableColumnsConstants.hideColumnSearchFunctionality, JSON.stringify(!this.showColumnSearchFields));

    this.searchControl.setValue('');
    this.filter.clearAllFilters();
    this.clearAllColumnSearchFields();
    this.columnSearchToggleChange.emit(this.showColumnSearchFields);
  }

  private clearAllColumnSearchFields() {
    this.columns.forEach(column => {
      column.searchValue = '';
      column.dateFromSearchValue = '';
      column.dateToSearchValue = '';
    });
    this.filterTable();
  }

  public itemsPerPageChanged() {
    this.saveCurrentPageSize();
    this.filterTable(true);
  }

  public filterTable(resetPagination = false) {
    this.loadingInProgress = true;

    if (resetPagination) {
      this.currentPage = 1;
      this.filter.pager.currentPage = 0;
    }

    if (this.serverSideProcessing) {
      if (this.getRowsSubscription && this.cancelOldGetRowsRequestOnNewRequest){
        this.getRowsSubscription.unsubscribe();
      }

      this.getRowsSubscription = this.service.getRows(this.filter, this.customParams)
        .pipe(finalize(() => this.loadingInProgress = false))
        .subscribe(
          (result: IPagedDataTable<any>) => {
            this.pageData(result);
          }
        );
    } else {
      this.clientProcessing();
      this.loadingInProgress = false;
    }


    if (this.actionButtonsComponent) {
      this.actionButtonsComponent.initButtons();
    }
  }

  public onColumnSearch(column: IColumn, componentItems: Array<ILookup> = null) {
    let searchText: string = '';
    if (componentItems && column.searchValue){
      searchText = componentItems.find(item => item.id === column.searchValue).text;
    }
    const eventData: ColumnSearchEventData = {
      column: column,
      searchText: searchText
    }
    this.columnSearchValueChangedSubscription.next(eventData);
  }

  public onPage(next: boolean): void {
    if (next) {
      this.filter.pager.currentPage++;
    } else {
      this.filter.pager.currentPage--;
    }
    this.currentPage = this.filter.pager.currentPage + 1;

    this.filterTable();
  }

  public onFirstLastPage(isFirstPage: boolean) {
    this.filter.pager.currentPage = isFirstPage ? 0 : this.totalPages - 1;
    this.currentPage = this.filter.pager.currentPage + 1;
    this.filterTable();
  }

  public goToPage() {
    if (this.currentPage > 0 && this.currentPage <= this.totalPages) {
      this.filter.pager.currentPage = +this.currentPage - 1;
      this.filterTable();
    } else {
      this.currentPage = this.filter.pager.currentPage + 1;
    }
  }

  public onSort(event) {
    this.filter.orderBy.name = event.column.sortingProp ? event.column.sortingProp : event.column.prop;
    this.filter.orderBy.direction = event.newValue === 'desc' ? OrderDirection.DESC : OrderDirection.ASC;
    this.saveOrderBy();
    this.filterTable();
    this.onSorted.emit();
  }

  public onFocusOutEvent(row) {
    this.onFocusOut.emit(row);
  }

  public getAllPagedData(): IPagedDataTable<any> {
    return this.createPagedRecordsFromRows();
  }


  public onKeyUp(event: KeyboardEvent, row: any, column: IColumn) {
    if (column.numberOptions?.minValue >= 0 && event.key === KeyboardCode.MINUS) {
      row[column.prop] = '';
    } else if (isNaN(row[column.prop])) {
      row[column.prop] = '';
    } else if (this.positiveNumbersWithoutZeroAllowed(column.numberOptions) && +row[column.prop] < 0) {
      row[column.prop] = '';
    }
    else if (!column.numberOptions?.includeMinValue && +row[column.prop] <= 0) {
      row[column.prop] = '';
    }

    this.changed(row, column.prop);
  }

  public applyDateFilters(column) {
    let dateFromFilter = this.getDateFilter(column.prop, FilterOperator.BiggerOrEqual);
    let dateToFilter = this.getDateFilter(column.prop, FilterOperator.SmallerOrEqual);

    if (this.userSettings.dateUtcStringToUserFormat(dateFromFilter.value) === column.dateFromSearchValue
        && (column.isSearchByPeriod ? this.userSettings.dateUtcStringToUserFormat(dateToFilter.value) === column.dateToSearchValue : true)) {
      return;
    }

    if (column.isSearchByPeriod) {
      dateFromFilter.value = column.dateFromSearchValue ? column.dateFromSearchValue : '';
      dateToFilter.value = column.dateToSearchValue ? column.dateToSearchValue : '';

      if (dateFromFilter.value && dateToFilter.value) {
        column.searchValue = `${dateFromFilter.value} - ${dateToFilter.value}`;
      } else if (dateFromFilter.value) {
        column.searchValue = `${this.translationService.translate('xsltAfter')} ${dateFromFilter.value}`
      } else {
        column.searchValue = `${this.translationService.translate('xsltBefore')} ${dateToFilter.value}`
      }

      if (!column.dateFromSearchValue && !column.dateToSearchValue) {
        this.emptyDateFilters(column);
      }
    } else {
      if (column.dateFromSearchValue) {
        dateFromFilter.value = column.dateFromSearchValue;
        dateToFilter.value = dateFromFilter.value;
        column.searchValue = column.dateFromSearchValue;
      } else {
        this.emptyDateFilters(column);
      }
    }

    this.filter.applyFilter();
  }

  public onDateFilterMenuOpen() {
    document.getElementById('dateFromInputField').focus();
  }

  public cancelGetRows(){
    this.getRowsSubscription.unsubscribe();
  }

  resetQuickFilter() {
    this.searchControl.setValue('');
  }

  public preventEnterKey(event: KeyboardEvent) {
    switch (event.key) {
      case KeyboardCode.ENTER || KeyboardCode.NUMPAD_ENTER:
        event.preventDefault();
        break;
      default:
        break;
    }
  }

  private emptyDateFilters(column) {
    this.getDateFilter(column.prop, FilterOperator.BiggerOrEqual).value = '';
    this.getDateFilter(column.prop, FilterOperator.SmallerOrEqual).value = '';
    column.searchValue = '';
    column.dateFromSearchValue = '';
    column.dateToSearchValue = '';
  }

  private positiveNumbersWithoutZeroAllowed(numberOptions: CpqNumberOptions): boolean {
    return numberOptions?.minValue > 0 || (numberOptions?.minValue == 0 && numberOptions?.includeMinValue == false);
  }

  public initColumns() {
    this.tableColumns = [];
    this.tableWidth = 0;
    this.setSelectedColumnsFromLocalStorage();

    this.columns.forEach(column => {
      if (column.visible && !column.hidden) {
        switch (column.cellType) {
          case CellType.default:
            column.cellTemplate = this.defaultCellTmpl;
            break;
          case CellType.toggle:
            column.cellTemplate = this.toggleCellTmpl;
            break;
          case CellType.edit:
            column.cellTemplate = this.editTmpl;
            break;
          case CellType.checkbox:
            column.cellTemplate = this.checkboxCellTmpl;
            break;
          case CellType.custom:
            break;
          case CellType.actions:
            column.width = 110;
            column.minWidth = 110;
            column.maxWidth = 110;
            column.resizeable = false;
            break;
          case CellType.twoStateCellTmpl:
            column.cellTemplate = this.twoStateCellTmpl;
            break;
          default:
            column.cellTemplate = this.defaultCellTmpl;
        }

        this.tableColumns.push(column);
      }

      if (!column.hidden) {
        this.setColumnHeaderTemplate(column);
      }

      if (!column.type && column.type !== 0) {
        column.type = ColumnType.Text;
      }
    });
  }

  private setColumnHeaderTemplate(column: IColumn) {
    if (!column.headerTemplate && this.showColumnSearchFields) {
      column.headerTemplate = this.defaultHeaderTemplate;
      if (column.searchFieldType === ColumnSearchFieldType.select && !column.columnSearchItems) {
        throw new Error(`In order to use select search field type, you have to provide columnSearchItems for column:${column.prop}`);
      }
    }
  }

  private setSelectedColumnsFromLocalStorage() {
    if (this.showColumnSelection) {
      const selectedColumns = JSON.parse(localStorage.getItem(this.guid + DatatableColumnsConstants.selectedColumns)) || [];
      if (selectedColumns.length > 0) {
        this.columns.forEach(column => column.visible = false);
        this.columns.forEach(column => {
          if (selectedColumns.includes(column.prop) && !column.hideInColumnSelection) {
            column.visible = true;
          }
        });
      }
    }
  }

  private saveActiveColumns() {
    if (this.showColumnSelection) {
      if (this.guid) {
        const visibleColumns = this.columns.map(c => {
          if (c.visible) {
            return c.prop;
          }
        });
        localStorage.setItem(this.guid + DatatableColumnsConstants.selectedColumns, JSON.stringify(visibleColumns));
      } else {
        throw new Error('Guid must be provided if you want to save column visibility state!');
      }
    }
  }

  private initClientSideProcessing(): void {
    this.loadingInProgress = true;
    this.emitTableAndRowsChange();
    this.filterTable();
    this.loadingInProgress = false;
  }

  private clientProcessing() {
    const pagedRecords = [];
    let filteredRecords = [];
    // quick filter
    if (this.filter.quickFilter) {
      this.rows.forEach(record => {
        const properties = this.filterProperties.length > 0 ? this.filterProperties : Object.getOwnPropertyNames(record);
        for (let i = 0; i < properties.length; i++) {
          const prop = properties[i];
          if (prop && record[prop] && record[prop].toString().toLowerCase().includes(this.filter.quickFilter.toLowerCase())) {
            filteredRecords.push(record);
            break;
          }
        }
      });
    } else {
      filteredRecords = clone<Array<any>>(this.rows);
    }

    // advanced filtering
    filteredRecords = this.applyClientAdvancedFilters(filteredRecords);
    filteredRecords = this.applyClientOrdering(filteredRecords);

    if (filteredRecords.length) {
      const startIndex = this.filter.pager.currentPage * this.filter.pager.itemsPerPage;
      const endIndex = (this.currentPage + 1) * this.filter.pager.itemsPerPage;

      for (let i = startIndex; i < endIndex; i++) {
        if (filteredRecords[i]) {
          pagedRecords.push(filteredRecords[i]);
        } else {
          break;
        }
      }
    }

    const pagedData: IPagedDataTable<any> = {
      pagedRecords: pagedRecords,
      totalNumberOfRecords: filteredRecords.length
    };
    this.pageData(pagedData);
  }

  private applyClientOrdering(filteredRecords: Array<any>): Array<any> {
    if (!this.filter.orderBy || !this.filter.orderBy.name) {
      return filteredRecords;
    }

    const column = this.columns.filter(x => (x.sortingProp ?? x.prop) === this.filter.orderBy.name)[0];
    if (!column) {
      return filteredRecords;
    }

    let prepareFn;
    switch (column.type) {
      case ColumnType.Date:
      case ColumnType.DateTime:
        prepareFn = this.userSettings.dateStringToTicks;
        break;
      default:
        prepareFn = null;
        break;
    }

    const comparator = new OrderComparator(
      column.type,
      this.filter.orderBy.direction,
      column.prop,
      prepareFn // add conversion date string -> number
    );

    return filteredRecords.sort(comparator.compare);
  }


  private applyAdvancedFilter(array: Array<any>, filter: Filter): Array<any> {
    if (!filter.value) {
      return array;
    }

    switch (filter.type) {
      case FilterType.Number: {
        if (isNaN(+filter.value)) {
          throw new Error('Filter type number requires number value.');
        }

        const comparator = new FilterComparator(filter.operator, filter.name, +filter.value);
        return array.filter(comparator.compare);
      }
      case FilterType.Select: {
        if (array.length) {
          if (typeof (array[0][filter.name]) === 'boolean') {
            const filterValue = filter.value !== '0' && filter.value !== '' && filter.value.toLowerCase() !== 'false';
            return array.filter(x => !!x[filter.name] === filterValue);
          }
          return array.filter(x => x[filter.name] && (x[filter.name] as string).indexOf(filter.valueText) > -1);
        }
      }
      case FilterType.Date: {
        if (filter.type == FilterType.Date && (filter.operator == FilterOperator.SmallerOrEqual || filter.operator == FilterOperator.Smaller)) {
          filter.value = hasValue(filter.value) ? this.userSettings.dateToIsoFormat(filter.value, this.userSettings.dateFormats.display.dateInput, true) : null;
        }
        if (filter.type == FilterType.Date && (filter.operator == FilterOperator.BiggerOrEqual || filter.operator == FilterOperator.Bigger)) {
          filter.value = hasValue(filter.value) ? this.userSettings.dateToIsoFormat(filter.value, this.userSettings.dateFormats.display.dateInput, false) : null;
        }

        const comparator = new FilterComparator(filter.operator, filter.name, filter.value, this.userSettings.dateReturnWithoutTime);
        return array.filter(comparator.compare);
      }
      default: {
        return array.filter(x => x[filter.name] && (x[filter.name] as string).toLowerCase().indexOf(filter.value.toLowerCase()) > -1);
      }
    }
  }

  private applyClientAdvancedFilters(filteredRecords: Array<any>) {
    let result = clone<Array<any>>(filteredRecords);
    this.filter.filters.forEach(filter => {
      result = this.applyAdvancedFilter(result, filter);
    });
    return result;
  }

  private pageData(pagedData: IPagedDataTable<any>) {
    this.tableRows = pagedData.pagedRecords;
    this.totalNumberOfRecords = pagedData.totalNumberOfRecords;

    if (this.totalNumberOfRecords === 0) {
      this.totalPages = 0;
      this.currentPage = 0;
    } else if (pagedData.pagedRecords.length !== 0) {
      this.totalPages =
        this.totalNumberOfRecords % this.filter.pager.itemsPerPage === 0 ?
          this.totalNumberOfRecords / this.filter.pager.itemsPerPage
          : Math.floor(this.totalNumberOfRecords / this.filter.pager.itemsPerPage) + 1;

      this.currentPage = this.filter.pager.currentPage + 1;

      if (this.modifiedRows.length > 0) {
        this.modifiedRows.forEach(row => {
          const index = this.tableRows.findIndex(r => r[this.rowIdentifier] === row[this.rowIdentifier]);
          if (index > -1) {
            this.tableRows[index] = row;
          }
        });
      }

      this.onRowsChanged.emit(pagedData);
    } else {
      this.filter.pager.currentPage--;
      this.filter.emitChange();
    }

    this.updateScrollbars();
  }

  private updateScrollbars() {
    const tableHeader = (this.table.element && this.table.element.parentElement) ? this.table.element.parentElement.querySelector('.datatable-header') as HTMLElement : null;
    const tableRowCenter = (tableHeader && tableHeader.parentElement) ? tableHeader.parentElement.querySelector('.datatable-row-center') as HTMLElement : null;
    const tableBody = (this.table.element && this.table.element.parentElement) ? this.table.element.parentElement.querySelector('.datatable-body') as HTMLElement : null;

    if (tableHeader && tableRowCenter && tableBody) {
      this.previousScrollPosition = tableBody.scrollLeft ? tableBody.scrollLeft : tableHeader.scrollLeft;

      if (this.totalNumberOfRecords === 0) {
        tableRowCenter.style.transform = 'translate3d(0, 0, 0)';
        tableHeader.classList.add('cpq-scroll');
        // setTimeout to make sure scroll position is updated after rendering of the header scroll is done
        setTimeout(() => tableHeader.scrollLeft = this.previousScrollPosition);
      } else {
        tableHeader.scrollTo(0, 0);
        tableHeader.classList.remove('cpq-scroll');
        // setTimeout to make sure scroll position is updated after rendering of the body scroll is done
        setTimeout(() => tableBody.scrollLeft = this.previousScrollPosition);
      }
    }
  }

  private emitTableAndRowsChange() {
    this.onRowsChanged.emit(this.createPagedRecordsFromRows());
  }

  public changed(row: any, prop: string) {
    // If we want to change row inputs row needs to have id
    if (row[this.rowIdentifier]) {
      removeIfExists(this.modifiedRows, x => x[this.rowIdentifier] === row[this.rowIdentifier]);
      this.modifiedRows.push(row);
      const rowChanges: RowChanges = { modifiedRows: this.modifiedRows, lastModifiedProperty: prop, lastModifiedRow: row };
      this.onRowChanged.next(rowChanges);
    } else if (this.onRowChanged.observers.length) {
      throw new Error(`'rowIdentifier' input must be defined, or value of row['rowIdentifier'] must be truthy.`);
    }
  }

  private saveOrderBy() {
    if (this.guid) {
      const sortInfo = JSON.parse(localStorage.getItem(AdvanceFilterConstants.orderBy)) || {};
      sortInfo[this.guid] = this.filter.orderBy;
      localStorage.setItem(AdvanceFilterConstants.orderBy, JSON.stringify(sortInfo));
    }
  }

  private saveCurrentPageSize() {
    if (this.guid) {
      localStorage.setItem(`${this.guid}_${AdvanceFilterConstants.pageSize}`, this.filter.pager.itemsPerPage.toString());
    }
  }

  private validateDataProcessingParameters(): void {
    if (this.serverSideProcessing) {
      this.validateServerSideProcessing();
    } else {
      this.validateClientSideProcessing();
    }
  }

  private validateServerSideProcessing(): void {
    if (!this.service) {
      throw new Error('Server side processing requires a IDataTableService instance.');
    }

    if (!this.isIDatatableService(this.service)) {
      throw new Error('Service must implement IDataTableService interface.');
    }

    if (this.filterProperties && this.filterProperties.length) {
      throw new Error('filterProperties can be use only for client processing.');
    }
  }

  private isIDatatableService(service: any): service is IDataTableService<any> {
    return (<IDataTableService<any>>service).getRows !== undefined;
  }

  private validateClientSideProcessing(): void {
    if (this.service) {
      throw new Error(`IDataTableService implementation can not be provided for client side processing. You must provide 'rows' input.`);
    }
    if (!this.rows) {
      throw new Error(`Client side processing requires a 'rows' input.`);
    }
  }

  private createPagedRecordsFromRows(): IPagedDataTable<any> {
    return { pagedRecords: this.rows, totalNumberOfRecords: this.rows.length };
  }

  private initFilters() {
    if (this.filter) {
      this.filter.advanceFiltersInitializedEvent.subscribe((isInit) => {
        if (isInit) {
          this.searchControl.setValue(this.filter.quickFilter);
          this.filter.applyFilter(false, false);
        }
      });
    }
  }

  private initQuickFilter() {
    this.searchControl.valueChanges
      .pipe(debounceTime(600), distinctUntilChanged(), filter(newValue => newValue !== this.filter.quickFilter))
      .subscribe(newValue => {
        this.filter.quickFilter = newValue;
        this.filter.pager.currentPage = 0;
        this.filter.saveQuickFilter();
        this.filter.emitChange();
      });
  }
}

